import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { useScrollTo } from '../hooks/useScrollTo';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Leistungen', path: '/leistungen' },
  { name: 'Über uns', path: '/ueber-uns' },
  { name: 'Karriere', path: '/jobs' },
  { name: 'Kontakt', path: '/kontakt' },
];

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { scrollToTop } = useScrollTo();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 bg-white shadow-soft py-2"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between">
          {/* Logo - 50% größer, kein Padding */}
          <Link 
            to="/" 
            onClick={scrollToTop}
            className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-platin-400 rounded"
            aria-label="ConciergeUnit - Zur Startseite"
          >
            <img 
              src="/logo.jpeg" 
              alt="ConciergeUnit" 
              className="h-16 md:h-20 lg:h-24 w-auto object-contain"
              style={{ maxWidth: 'none' }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = target.parentElement?.querySelector('.logo-fallback') as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            <div className="logo-fallback hidden h-16 md:h-20 lg:h-24 w-16 md:w-20 lg:w-24 bg-brand-950 rounded items-center justify-center">
              <span className="text-white font-bold text-xl md:text-2xl">CU</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10" aria-label="Hauptnavigation">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-sm font-medium tracking-wide transition-colors duration-300 py-2 ${
                  isActive(link.path)
                    ? 'text-brand-950'
                    : 'text-gray-600 hover:text-brand-950'
                }`}
                aria-current={isActive(link.path) ? 'page' : undefined}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-platin-400 rounded-full" />
                )}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-6">
            <a
              href={`tel:${'+4917672536937'.replace(/\s/g, '')}`}
              className="flex items-center gap-2 text-sm font-medium text-brand-950 hover:text-brand-700 transition-colors"
              aria-label="Anrufen"
            >
              <Phone className="w-4 h-4" aria-hidden="true" />
              <span className="hidden xl:inline">+49 176 72536937</span>
            </a>
            <Link
              to="/kontakt"
              className="bg-brand-950 text-white px-6 py-3 rounded-lg font-medium text-sm
                       hover:bg-brand-800 transition-all duration-300 
                       hover:shadow-luxury hover:-translate-y-0.5
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-platin-400 focus-visible:ring-offset-2"
            >
              Kontakt
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-3 rounded-lg hover:bg-platin-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-platin-400"
            aria-expanded={isMobileMenuOpen}
            aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-brand-950" aria-hidden="true" />
            ) : (
              <Menu className="w-6 h-6 text-brand-950" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        className={`lg:hidden fixed inset-x-0 top-[88px] md:top-[104px] bg-white shadow-luxury transition-all duration-300 max-h-[calc(100vh-104px)] overflow-y-auto ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        role="navigation"
        aria-label="Mobile Navigation"
      >
        <nav className="container-custom py-6 flex flex-col gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-4 rounded-lg text-lg font-medium transition-all ${
                isActive(link.path)
                  ? 'bg-brand-950 text-white'
                  : 'text-gray-700 hover:bg-platin-100'
              }`}
              aria-current={isActive(link.path) ? 'page' : undefined}
            >
              {link.name}
            </Link>
          ))}
          <hr className="my-4 border-platin-200" />
          <a
            href={`tel:${'+4917672536937'.replace(/\s/g, '')}`}
            className="flex items-center gap-3 px-4 py-4 text-brand-950 font-medium"
          >
            <Phone className="w-5 h-5" aria-hidden="true" />
            <span>+49 176 72536937</span>
          </a>
          <Link
            to="/kontakt"
            className="mx-4 mt-2 bg-brand-950 text-white text-center px-6 py-4 rounded-lg font-medium 
                     hover:bg-brand-800 transition-colors"
          >
            Kontakt aufnehmen
          </Link>
        </nav>
      </div>
    </header>
  );
}
